import styled from 'styled-components'
import { useEffect, useState } from 'react'

import CheckoutPanel from 'checkout/components/CheckoutPanel'
import PaymentType from 'shared/enums/PaymentType'
import { AddressFormContextProvider } from 'checkout/containers/AddressFormContextProvider'
import { CheckoutQA } from 'shared/dataAttributes'
import { CreditCardVerification } from 'checkout/panels/PaymentMethod/CreditCardVerification'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { NewCardNewAddressForm } from 'checkout/panels/PaymentMethod/NewCardNewAddressForm'
import { PaymentMethodSummary } from 'checkout/panels/PaymentMethod/PaymentMethodSummary'
import { SVGIcon } from 'shared/components/Icons'
import { SavedPaymentMethodsForm } from 'checkout/panels/PaymentMethod/SavedPaymentMethodsForm'
import { t } from 'localization'
import { useCheckoutContext } from 'checkout/containers/CheckoutContextProvider'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { useGetAddressQuery } from 'api/addressesApi'
import { useGetBillingInfoQuery } from 'api/billingInfosApi'

const TP = 'checkout.panels.PaymentMethod.PaymentMethodPanel'

export const PaymentMethodPanel = () => {
  const { editPanel, order, panels } = useCheckoutContext()
  const isExposedPaymentMethods = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_EXPOSED_PAYMENT_METHODS)
  const [skipGetBillingInfo, setSkipGetBillingInfo] = useState(true)
  const [skipGetAddress, setSkipGetAddress] = useState(true)

  const { data: billingInfo } = useGetBillingInfoQuery(order?.billingInfoId, {
    skip: skipGetBillingInfo,
  })

  const { data: billingAddress } = useGetAddressQuery(billingInfo?.billingAddressId, {
    skip: skipGetAddress,
  })

  const getPaymentMethodView = () => {
    switch (panels.paymentMethod.view) {
      case 'saved':
        return <SavedPaymentMethodsForm currentBillingInfo={billingInfo} />
      case 'verification':
        return <CreditCardVerification billingAddress={billingAddress} billingInfo={billingInfo} />
      default:
        // case: 'new'
        if (isExposedPaymentMethods) {
          return <SavedPaymentMethodsForm currentBillingInfo={billingInfo} />
        }
        return <NewCardNewAddressForm />
    }
  }

  // If a user returns from Affirm/Express Pay/AfterPay/Klarna/PayPal without completing their order,
  // set payment panel to edit state
  useEffect(() => {
    const shouldEditPaymentMethod =
      billingInfo?.paymentType === PaymentType.Affirm ||
      billingInfo?.paymentType === PaymentType.ApplePay ||
      billingInfo?.paymentType === PaymentType.AfterPay ||
      billingInfo?.paymentType === PaymentType.Klarna ||
      billingInfo?.paymentType === PaymentType.PayPalv2

    if (shouldEditPaymentMethod) {
      editPanel('paymentMethod')
    }
  }, [billingInfo?.paymentType, editPanel])

  useEffect(() => {
    if (order?.billingInfoId) {
      setSkipGetBillingInfo(false)
    }
  }, [order?.billingInfoId])

  useEffect(() => {
    if (billingInfo?.billingAddressId) {
      setSkipGetAddress(false)
    }
  }, [billingInfo?.billingAddressId])

  return (
    <AddressFormContextProvider>
      <CheckoutPanel
        name="paymentMethod"
        title={
          <Title>
            <LockIcon />
            {t(`${TP}.paymentMethod`, 'Payment Method')}
          </Title>
        }
        qaAttr={CheckoutQA.PaymentMethod}
        expandedContent={getPaymentMethodView()}
        summarizedContent={
          <PaymentMethodSummary
            billingAddress={isExposedPaymentMethods ? null : billingAddress}
            billingInfo={billingInfo}
          />
        }
      />
    </AddressFormContextProvider>
  )
}

const Title = styled.div`
  display: flex;
  align-items: center;
  text-transform: uppercase;
`
const LockIcon = styled(SVGIcon).attrs({
  name: 'lock',
})`
  margin-right: 10px;
`
